import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { X, User, Briefcase, Star, TrendingUp, MessageSquare, FileText } from 'lucide-react';

function ratingColor(r) {
  var n = parseInt(r);
  if (n >= 5) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (n >= 4) return 'bg-emerald-50 text-emerald-600 border-emerald-200';
  if (n >= 3) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-red-50 text-red-700 border-red-200';
}

function FieldRow({ label, value }) {
  return (
    <div className="flex justify-between items-start py-2 border-b border-slate-50 last:border-0">
      <span className="text-xs font-medium text-muted-foreground w-2/5 shrink-0">{label}</span>
      <span className="text-sm text-right">{value || '-'}</span>
    </div>
  );
}

function SectionBlock({ title, Icon, children }) {
  return (
    <div className="space-y-1" data-testid={'detail-section-' + title.toLowerCase().replace(/\s/g, '-')}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-slate-500" />
        <h4 className="text-sm font-semibold">{title}</h4>
      </div>
      <div className="bg-slate-50/50 rounded-lg px-4 py-2">
        {children}
      </div>
    </div>
  );
}

export default function ProtoDetailSheet({ emp, open, onClose }) {
  if (!emp) return null;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="proto-detail-sheet">
        <div className="sticky top-0 bg-white z-10 border-b border-slate-200 px-6 py-4">
          <SheetHeader>
            <div className="flex items-start justify-between">
              <div>
                <SheetTitle className="text-xl font-bold">{emp.name}</SheetTitle>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Emp #{emp.empId} &middot; {emp.department} &middot; {emp.company}
                </p>
              </div>
              <Badge className={'text-sm font-bold border px-3 py-1 ' + ratingColor(emp.overallRating)} variant="outline">
                {emp.overallRating}/5
              </Badge>
            </div>
          </SheetHeader>
        </div>

        <div className="px-6 py-5 space-y-5">
          <SectionBlock title="Employee Information" Icon={User}>
            <FieldRow label="Full Name" value={emp.name} />
            <FieldRow label="Email" value={emp.email} />
            <FieldRow label="Employee ID" value={'#' + emp.empId} />
            <FieldRow label="Department" value={emp.department} />
            <FieldRow label="Company" value={emp.company} />
            <FieldRow label="Job Title" value={emp.jobTitle} />
            <FieldRow label="Date of Joining" value={emp.dateOfJoining} />
          </SectionBlock>

          <Separator />

          <SectionBlock title="Review Period" Icon={Briefcase}>
            <FieldRow label="Review Period Start" value={emp.reviewPeriodStart} />
            <FieldRow label="Review Period End" value={emp.reviewPeriodEnd} />
            <FieldRow label="Available Periods" value={emp.periods.join(', ')} />
            <FieldRow label="Total Submissions" value={emp.submissionCount} />
          </SectionBlock>

          <Separator />

          <SectionBlock title="Performance Ratings" Icon={Star}>
            {Object.entries(emp.ratings).map(function(entry) {
              return <FieldRow key={entry[0]} label={entry[0]} value={entry[1]} />;
            })}
            <FieldRow label="Overall Rating" value={emp.overallRating + ' / 5'} />
          </SectionBlock>

          <Separator />

          <SectionBlock title="Compensation & Increment" Icon={TrendingUp}>
            <FieldRow label="Current Salary" value={Number(emp.currentSalary).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })} />
            <FieldRow label="Suggested Increment" value={emp.suggestedIncrement + '%'} />
            <FieldRow label="Increment Start Date" value={emp.incrementStartDate} />
            <FieldRow label="Management Approval" value={emp.managementApproval} />
            <FieldRow label="Increment Letter" value={emp.incrementLetter} />
          </SectionBlock>

          <Separator />

          <SectionBlock title="Remarks & Observations" Icon={MessageSquare}>
            {Object.entries(emp.remarks).map(function(entry) {
              return <FieldRow key={entry[0]} label={entry[0]} value={entry[1]} />;
            })}
          </SectionBlock>

          <Separator />

          <SectionBlock title="Submission Info" Icon={FileText}>
            <FieldRow label="Filled By" value={emp.filledBy} />
            <FieldRow label="Added Time" value={emp.addedTime} />
            <FieldRow label="Last Submission" value={new Date(emp.lastSubmission).toLocaleString('en-IN')} />
          </SectionBlock>
        </div>
      </SheetContent>
    </Sheet>
  );
}
